import { useCallback } from 'react';

import { ICombo, IComboSlot, PremiumComboSlotPricingVarations } from '@rbi-ctg/menu';
import { MenuObjectTypes } from 'enums/menu';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { isLocalDev } from 'utils/environment';
import logger from 'utils/logger';
import { isCombo, isItem } from 'utils/menu';
import { ModifierComponentStyle } from 'utils/menu/modifiers';
import {
  isComboSlotSelectionPremium,
  isOptionInComboSlot,
  priceForItemOptionModifierBooleanOrStepper,
  priceForItemOptionModifierSelection,
} from 'utils/menu/price';
import { IWithVendorConfig } from 'utils/vendor-config';

import {
  IPriceForComboSlotSelectionFn,
  IPriceForItemInComboSlotSelectionFn,
  IPriceForItemOptionModifierFn,
  IPricingFunctionFn,
  IUsePricingFunctionHook,
} from './types';
import {
  priceForComboSelections,
  priceForItemModifierSelections,
  priceForPremiumComboSlotComposite,
  priceForPremiumComboSlotDelta,
  pricingFunctionNoSelections,
} from './utils';

export const usePricingFunction: IUsePricingFunctionHook = ({
  prices: storeContextPrices,
  vendor: storeContextVendor,
}) => {
  const premiumComboSlotPricingMethod = useFlag<PremiumComboSlotPricingVarations>(
    LaunchDarklyFlag.ENABLE_PREMIUM_COMBO_SLOTS
  );

  const priceForItemInComboSlotSelection = useCallback<IPriceForItemInComboSlotSelectionFn>(
    ({
      combo,
      comboSlot,
      selectedItem,
      // This allows you to override the method if needed
      // If you do not want to use the value given by the flag pass in your own method
      pricingMethod = 'none',
      prices = storeContextPrices,
      vendor = storeContextVendor,
    }) => {
      const overrideMethod =
        pricingMethod === 'none' ? premiumComboSlotPricingMethod ?? 'none' : pricingMethod;

      switch (overrideMethod) {
        case 'composite':
          return priceForPremiumComboSlotComposite(combo as ICombo, selectedItem, prices, vendor);

        case 'delta':
          return priceForPremiumComboSlotDelta(
            comboSlot as IComboSlot,
            selectedItem,
            prices,
            vendor
          );

        case 'direct':
          return pricingFunctionNoSelections(selectedItem as IWithVendorConfig, prices, vendor);

        case 'none':
          return 0;

        default: {
          if (isLocalDev) {
            logger.warn({
              message: '[priceForComboSlotSelection] You are using an unrecognized pricingMethod',
              pricingMethod: overrideMethod,
            });
          }
          return 0;
        }
      }
    },
    [premiumComboSlotPricingMethod, storeContextPrices, storeContextVendor]
  );

  const priceForComboSlotSelection = useCallback<IPriceForComboSlotSelectionFn>(
    ({
      combo,
      comboSlot,
      selectedOption,
      // This allows you to override the method if needed
      // If you do not want to use the value given by the flag pass in your own method
      pricingMethod = 'none',
      prices = storeContextPrices,
      vendor = storeContextVendor,
    }) => {
      // return instantly if the selectedOption doesn't exist in the comboSlot... Returning zero. Its on the caller to identify what to do.
      if (!isOptionInComboSlot(comboSlot, selectedOption)) {
        return 0;
      }
      // ComboSlots are always free unless they are explicitely marked as premium
      if (!selectedOption || !isComboSlotSelectionPremium(selectedOption)) {
        return 0;
      }
      const selectedItem = selectedOption?.option;

      if (!selectedItem || selectedItem._type === MenuObjectTypes.PICKER) {
        return 0;
      }
      return priceForItemInComboSlotSelection({
        combo,
        comboSlot,
        pricingMethod,
        selectedItem,
        prices,
        vendor,
      });
    },
    [priceForItemInComboSlotSelection, storeContextPrices, storeContextVendor]
  );

  const priceForItemOptionModifier = useCallback<IPriceForItemOptionModifierFn>(
    ({ item, itemOption, modifier, prices = storeContextPrices }) => {
      if (!item || !itemOption || !modifier || isCombo(item)) {
        return 0;
      }

      if (
        !itemOption.componentStyle ||
        itemOption.componentStyle === ModifierComponentStyle.Selector
      ) {
        return priceForItemOptionModifierSelection({
          item,
          itemOption,
          modifier,
          prices,
          vendor: storeContextVendor,
        });
      }

      return priceForItemOptionModifierBooleanOrStepper({
        item,
        itemOption,
        modifier,
        prices,
        vendor: storeContextVendor,
      });
    },
    [storeContextPrices, storeContextVendor]
  );

  /**
   * Returns the price of a menu object
   */
  // TODO: change to named params
  const pricingFunction = useCallback<IPricingFunctionFn>(
    ({
      item,
      quantity = 1,
      modifierSelections = [],
      comboSlotSelections = {},
      prices = storeContextPrices,
      vendor = storeContextVendor,
    }) => {
      const itemQuantity = quantity || 1;
      const price = pricingFunctionNoSelections(item, prices, vendor, itemQuantity);

      if (isItem(item)) {
        return (
          price +
          priceForItemModifierSelections({
            item,
            quantity,
            modifierSelections,
            prices,
            priceForItemOptionModifier,
          })
        );
      }

      if (isCombo(item)) {
        return (
          price +
          priceForComboSelections({
            combo: item,
            quantity,
            modifierSelections,
            comboSlotSelections,
            prices,
            vendor,
            priceForComboSlotSelection,
            priceForItemOptionModifier,
          })
        );
      }

      return price;
    },
    [priceForComboSlotSelection, priceForItemOptionModifier, storeContextPrices, storeContextVendor]
  );

  return {
    priceForItemInComboSlotSelection,
    priceForComboSlotSelection,
    priceForItemOptionModifier,
    pricingFunction,
  };
};
